import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, TextField, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, InputAdornment,
  FormControl, InputLabel, Select, MenuItem, Pagination, CircularProgress,
  Tabs, Tab, Divider, Alert, Snackbar, Tooltip
} from '@mui/material';
import {
  Search as SearchIcon, Refresh as RefreshIcon, CheckCircle as SuccessIcon,
  Cancel as FailIcon, Pending as PendingIcon, Visibility as ViewIcon,
  Download as DownloadIcon, FilterList as FilterIcon, Person as PersonIcon,
  Phone as PhoneIcon, Email as EmailIcon, Save as SaveIcon, Note as NoteIcon
} from '@mui/icons-material';
import axios from 'axios';
import { api_urls } from '../../utils/api-urls';

const ADMIN_STATUS_OPTIONS = [
  { value: '', label: 'No Status', color: '#9E9E9E' },
  { value: 'MANUAL_DONE', label: 'Manual Done', color: '#4CAF50' },
  { value: 'PAYMENT_FAILED', label: 'Payment Failed', color: '#f44336' },
  { value: 'REFUNDED', label: 'Refunded', color: '#2196F3' },
  { value: 'INVESTIGATING', label: 'Investigating', color: '#FF9800' },
  { value: 'RESOLVED', label: 'Resolved', color: '#8BC34A' },
];

const Utilities = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [stats, setStats] = useState({
    total: 0, success: 0, failed: 0, pending: 0, totalAmount: 0
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [savingId, setSavingId] = useState(null);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page, limit, search, status: statusFilter,
        service: serviceFilter, provider: providerFilter,
        dateFrom, dateTo
      });
      const response = await axios.get(
        `${api_urls}api/app/customer/recharge-history?${params}`
      );
      if (response.data.success) {
        setTransactions(response.data.data?.transactions || response.data.data || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setStats(response.data.stats || stats);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTransactions();
  }, [page, limit, statusFilter, serviceFilter, providerFilter]);

  const handleSearch = () => {
    setPage(1);
    fetchTransactions();
  };

  const handleAdminStatusChange = async (transactionId, newStatus) => {
    try {
      // Update local state immediately
      setTransactions(prev => prev.map(t =>
        t._id === transactionId ? { ...t, adminStatus: newStatus } : t
      ));
      // Save to backend
      await axios.put(`${api_urls}api/app/customer/recharge-admin-update`, {
        transactionId, adminStatus: newStatus
      });
      setSnackbar({ open: true, message: 'Admin status updated', severity: 'success' });
    } catch (error) {
      console.error('Error updating admin status:', error);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  };

  const handleAdminNotesSave = async (transactionId, notes) => {
    setSavingId(transactionId);
    try {
      await axios.put(`${api_urls}api/app/customer/recharge-admin-update`, {
        transactionId, adminNotes: notes
      });
      setSnackbar({ open: true, message: 'Notes saved', severity: 'success' });
    } catch (error) {
      console.error('Error saving notes:', error);
      setSnackbar({ open: true, message: 'Failed to save notes', severity: 'error' });
    }
    setSavingId(null);
  };

  const handleDetailAdminNotesSave = async () => {
    if (!selectedTransaction) return;
    setSavingId(selectedTransaction._id);
    try {
      await axios.put(`${api_urls}api/app/customer/recharge-admin-update`, {
        transactionId: selectedTransaction._id,
        adminNotes: selectedTransaction.adminNotes || '',
        adminStatus: selectedTransaction.adminStatus || ''
      });
      // Update in main list too
      setTransactions(prev => prev.map(t =>
        t._id === selectedTransaction._id
          ? { ...t, adminNotes: selectedTransaction.adminNotes, adminStatus: selectedTransaction.adminStatus }
          : t
      ));
      setSnackbar({ open: true, message: 'Admin notes saved', severity: 'success' });
    } catch (error) {
      console.error('Error saving notes:', error);
      setSnackbar({ open: true, message: 'Failed to save notes', severity: 'error' });
    }
    setSavingId(null);
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      success: { color: 'success', icon: <SuccessIcon fontSize="small" />, label: 'Success' },
      failed: { color: 'error', icon: <FailIcon fontSize="small" />, label: 'Failed' },
      failure: { color: 'error', icon: <FailIcon fontSize="small" />, label: 'Failed' },
      pending: { color: 'warning', icon: <PendingIcon fontSize="small" />, label: 'Pending' },
      refunded: { color: 'info', icon: <RefreshIcon fontSize="small" />, label: 'Refunded' }
    };
    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
    return (
      <Chip size="small" icon={config.icon} label={config.label} color={config.color} variant="outlined" />
    );
  };

  const getAdminStatusChip = (adminStatus) => {
    if (!adminStatus) return null;
    const opt = ADMIN_STATUS_OPTIONS.find(o => o.value === adminStatus);
    if (!opt) return null;
    return (
      <Chip size="small" label={opt.label} sx={{ bgcolor: opt.color, color: 'white', fontWeight: 600, fontSize: '0.7rem' }} />
    );
  };

  const getProviderChip = (provider) => {
    const normalized = (provider || '').toUpperCase();
    const labelMap = { PAY2NEW: 'Pay2New', A1TOPUP: 'A1Topup', CYRUS: 'Cyrus' };
    const colorMap = { PAY2NEW: '#4CAF50', A1TOPUP: '#2196F3', CYRUS: '#FF9800' };
    return (
      <Chip size="small" label={labelMap[normalized] || (provider && provider !== 'UNKNOWN' ? provider : 'Legacy')}
        sx={{ bgcolor: colorMap[normalized] || '#9E9E9E', color: 'white', fontWeight: 600 }} />
    );
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const StatsCard = ({ title, value, icon, color, subtitle }) => (
    <Card sx={{ height: '100%', bgcolor: color + '10', borderLeft: `4px solid ${color}` }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" variant="body2">{title}</Typography>
            <Typography variant="h4" fontWeight="bold" color={color}>{value}</Typography>
            {subtitle && <Typography variant="caption" color="textSecondary">{subtitle}</Typography>}
          </Box>
          <Box sx={{ color: color, opacity: 0.5 }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Utilities & Recharge History</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Monitor all recharge transactions, success/failure rates, and provider performance
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatsCard title="Total Transactions" value={stats.total} color="#1976d2" icon={<FilterIcon sx={{ fontSize: 40 }} />} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatsCard title="Successful" value={stats.success} color="#4CAF50" icon={<SuccessIcon sx={{ fontSize: 40 }} />}
            subtitle={`${((stats.success / stats.total) * 100 || 0).toFixed(1)}% success rate`} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatsCard title="Failed" value={stats.failed} color="#f44336" icon={<FailIcon sx={{ fontSize: 40 }} />} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatsCard title="Pending" value={stats.pending} color="#FF9800" icon={<PendingIcon sx={{ fontSize: 40 }} />} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatsCard title="Total Amount" value={`₹${(stats.totalAmount || 0).toLocaleString()}`} color="#9C27B0" icon={<DownloadIcon sx={{ fontSize: 40 }} />} />
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" placeholder="Search by mobile, transaction ID..."
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()} />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Service</InputLabel>
                <Select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} label="Service">
                  <MenuItem value="all">All Services</MenuItem>
                  <MenuItem value="mobile">Mobile</MenuItem>
                  <MenuItem value="dth">DTH</MenuItem>
                  <MenuItem value="electricity">Electricity</MenuItem>
                  <MenuItem value="gas">Gas</MenuItem>
                  <MenuItem value="fastag">FASTag</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Provider</InputLabel>
                <Select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)} label="Provider">
                  <MenuItem value="all">All Providers</MenuItem>
                  <MenuItem value="Pay2New">Pay2New</MenuItem>
                  <MenuItem value="A1Topup">A1Topup</MenuItem>
                  <MenuItem value="Cyrus">Cyrus</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField fullWidth size="small" type="date" label="From" value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField fullWidth size="small" type="date" label="To" value={dateTo}
                onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Recent Transactions</Typography>
            <Box display="flex" gap={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select value={limit} onChange={(e) => setLimit(e.target.value)}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
              <IconButton onClick={fetchTransactions} color="primary"><RefreshIcon /></IconButton>
            </Box>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>User Details</strong></TableCell>
                    <TableCell><strong>Service</strong></TableCell>
                    <TableCell><strong>Recharge Number</strong></TableCell>
                    <TableCell><strong>Amount</strong></TableCell>
                    <TableCell><strong>Provider</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Admin Status</strong></TableCell>
                    <TableCell><strong>Admin Notes</strong></TableCell>
                    <TableCell><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography color="textSecondary" py={3}>No transactions found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((t, idx) => (
                      <TableRow key={t._id || idx} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(t.createdAt || t.date)}</TableCell>
                        <TableCell>
                          <Box sx={{ minWidth: 120 }}>
                            {t.userName && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <PersonIcon sx={{ fontSize: 14, color: '#666' }} />
                                <Typography variant="body2" fontWeight="bold">{t.userName}</Typography>
                              </Box>
                            )}
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <PhoneIcon sx={{ fontSize: 14, color: '#1976d2' }} />
                              <Typography variant="body2" color="primary">{t.userPhone || t.mobile || '-'}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell><Chip size="small" label={t.billType || t.serviceType || t.type || 'Mobile'} /></TableCell>
                        <TableCell>{t.number || t.mobile || t.accountNumber || '-'}</TableCell>
                        <TableCell><strong>₹{t.amount}</strong></TableCell>
                        <TableCell>{getProviderChip(t.provider)}</TableCell>
                        <TableCell>{getStatusChip(t.status)}</TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={t.adminStatus || ''}
                              onChange={(e) => handleAdminStatusChange(t._id, e.target.value)}
                              displayEmpty
                              sx={{ fontSize: '0.75rem' }}
                            >
                              {ADMIN_STATUS_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: opt.color }} />
                                    <Typography variant="body2">{opt.label}</Typography>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <TextField
                              size="small"
                              placeholder="Add note..."
                              defaultValue={t.adminNotes || ''}
                              sx={{ minWidth: 120, '& input': { fontSize: '0.75rem', py: 0.5 } }}
                              onBlur={(e) => {
                                if (e.target.value !== (t.adminNotes || '')) {
                                  handleAdminNotesSave(t._id, e.target.value);
                                }
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleAdminNotesSave(t._id, e.target.value);
                                  e.target.blur();
                                }
                              }}
                            />
                            {savingId === t._id && <CircularProgress size={16} />}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="primary" onClick={() => { setSelectedTransaction({...t}); setDetailsOpen(true); }}>
                            <ViewIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination count={totalPages} page={page} onChange={(e, p) => setPage(p)} color="primary" />
          </Box>
        </CardContent>
      </Card>

      {/* Transaction Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white' }}>
          Transaction Details - #{selectedTransaction?.transactionId || selectedTransaction?._id?.slice(-8)}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedTransaction && (
            <Grid container spacing={2}>
              {/* User Details Section */}
              <Grid item xs={12}>
                <Box sx={{ bgcolor: '#e3f2fd', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    <PersonIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    User Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="textSecondary">Name</Typography>
                      <Typography fontWeight="bold">{selectedTransaction.userName || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
                      <Typography fontWeight="bold" color="primary">{selectedTransaction.userPhone || selectedTransaction.mobile || '-'}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                      <Typography fontWeight="bold" sx={{ wordBreak: 'break-all' }}>{selectedTransaction.userEmail || 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              
              {/* Transaction Details */}
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">Service</Typography>
                <Typography fontWeight="bold">{selectedTransaction.billType || selectedTransaction.serviceType || selectedTransaction.type}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">Provider</Typography>
                {getProviderChip(selectedTransaction.provider)}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">Recharge Number</Typography>
                <Typography fontWeight="bold">{selectedTransaction.number || selectedTransaction.mobile || selectedTransaction.accountNumber || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">Operator</Typography>
                <Typography>{selectedTransaction.productName || selectedTransaction.operator || selectedTransaction.operatorName || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                <Typography variant="h6" color="primary">₹{selectedTransaction.amount}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                {getStatusChip(selectedTransaction.status)}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Transaction ID</Typography>
                <Typography sx={{ wordBreak: 'break-all' }}>{selectedTransaction.transactionId || selectedTransaction._id || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Operator Reference</Typography>
                <Typography sx={{ wordBreak: 'break-all' }}>{selectedTransaction.operator_reference || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Date & Time</Typography>
                <Typography>{formatDate(selectedTransaction.createdAt || selectedTransaction.date)}</Typography>
              </Grid>
              {(selectedTransaction.errorDetails || selectedTransaction.failReason || selectedTransaction.primaryApiError) && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>Failure Reason:</strong> {selectedTransaction.errorDetails || selectedTransaction.failReason || selectedTransaction.primaryApiError}
                  </Alert>
                </Grid>
              )}

              {/* Admin Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" fontWeight="bold" color="secondary" gutterBottom sx={{ mt: 1 }}>
                  <NoteIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Admin Management
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Admin Status</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={selectedTransaction.adminStatus || ''}
                    onChange={(e) => setSelectedTransaction(prev => ({ ...prev, adminStatus: e.target.value }))}
                  >
                    {ADMIN_STATUS_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: opt.color }} />
                          {opt.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                {selectedTransaction.adminUpdatedAt && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Last Admin Update</Typography>
                    <Typography variant="body2">{formatDate(selectedTransaction.adminUpdatedAt)}</Typography>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>Admin Notes</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add admin notes about this transaction..."
                  value={selectedTransaction.adminNotes || ''}
                  onChange={(e) => setSelectedTransaction(prev => ({ ...prev, adminNotes: e.target.value }))}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={savingId === selectedTransaction?._id ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleDetailAdminNotesSave}
            disabled={savingId === selectedTransaction?._id}
          >
            Save Admin Notes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Utilities;
